import { DateTime } from 'luxon';
import { DRAFT_MAX_AGE_DAYS, IST_ZONE, type ArrangementDto } from '@pams/shared';
import { DRAFT_EXPIRY_COPY } from './copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C5c §8] WHEN THE SWEEP WILL DELETE THIS DRAFT — the builder's half of the thirty-day rule.
 *
 *  PURE, AND `now` IS A PARAMETER: the page passes the clock, the tests pass their own. It reads
 *  the SAME three facts the server's `staleDraftSelector` reads — a DRAFT, not archived, with a
 *  `draftSince` — and adds the same `DRAFT_MAX_AGE_DAYS` × 24 h, so the date on the screen is the
 *  date the selector would first match. An archived draft has the Archive's own window instead, an
 *  issued order has no clock at all, and a legacy draft the backfill has not reached is never swept
 *  — so all three answer `null` and show nothing.
 *
 *  ⚠️ THE DATE IS SHOWN IN IST, FORMATTED BY LUXON — never `toISOString().slice(0, 10)`, which is
 *  the UTC day and is YESTERDAY for five and a half hours of every one ([IST-FIX]).
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** The last week — long enough to act on, short enough not to nag a draft started yesterday. */
export const DRAFT_EXPIRY_NOTICE_DAYS = 7;

const DAY_MS = 24 * 60 * 60 * 1000;

type DraftClock = Pick<ArrangementDto, 'draftSince' | 'archivedAt'> & { status: string };

/*
 * ⚠️ `typeof … === 'string'`, NEVER `!== null`, AND AN UNPARSEABLE CLOCK IS NO CLOCK. A web build
 * ahead of its API (or a detail cached before the column) carries no `draftSince` key at all, and
 * `!== null` read that as a clock and printed "deleted on Invalid DateTime" on every draft. Both
 * fail CLOSED — the notice is silent, as it is for a legacy draft the sweep never touches.
 */
export const draftDeletesAt = (record: DraftClock): Date | null => {
  if (record.status !== 'DRAFT' || record.archivedAt !== null || typeof record.draftSince !== 'string') return null;
  const since = Date.parse(record.draftSince);
  return Number.isNaN(since) ? null : new Date(since + DRAFT_MAX_AGE_DAYS * DAY_MS);
};

/** Past its thirty days — the only case in which a 404 on a save may be read as "it was deleted". */
export const draftHasExpired = (record: DraftClock, now: Date): boolean => {
  const at = draftDeletesAt(record);
  return at !== null && at.getTime() <= now.getTime();
};

export const draftExpiryNotice = (record: DraftClock, now: Date): string | null => {
  const at = draftDeletesAt(record);
  if (at === null) return null;
  const left = at.getTime() - now.getTime();
  if (left > DRAFT_EXPIRY_NOTICE_DAYS * DAY_MS) return null;
  if (left <= 0) return DRAFT_EXPIRY_COPY.imminent;
  return DRAFT_EXPIRY_COPY.notice(DateTime.fromJSDate(at, { zone: IST_ZONE }).toFormat('d LLL yyyy'));
};
